// Every loading phrase PretorOne shows. The animated loader
// (main-busy-indicator.js, mobile widths only: desktop keeps the old gif) shuffles the list
// for what is loading and shows a new line every few seconds.
//
// How a loader picks its list:
//   1. A loader inside a page (a memo, a details panel, an upload) names its list with
//      data-loading-phrases="<key>" on the <section class="main-busy-indicator">:
//      PANELS[key]. "none" shows the animation with no message (the rentals success and
//      failure dialogs, which say what happened themselves).
//   2. Otherwise the screen's ui-router state name (common/feature-enum.js): SCREENS[state].
//      A child state such as compliance.fire uses its parent's list.
//   3. Otherwise FALLBACK.
//
// The lines are there to make the wait funny: a small joke about the screen being loaded,
// never at the expense of the person reading it (or of owners who are behind on levies).
// Each screen and panel has lines of its own: no phrase appears in more than one list.
// Screens with no loader of their own (login, trustee-dashboard, find-a-contractor) have no
// list. Write new lines like the rest: present tense, ending in an ellipsis (…), with curly
// apostrophes (’) and quotes (“ ”).

export const SCREENS = {
    'dashboard': [
        'Bribing the hamster that powers the server…',
        'Asking the hadedas to keep it down…',
        'Telling the lift it’s a very good lift…',
        'Persuading the gate motor that it wants to open…',
        'Feeding the building cat (it insists)…',
        'Untangling the Christmas lights from 2019…',
        'Looking for the remote. It’s always in the couch…',
        'Finding out who parked in your bay…',
        'Learning the security guard’s secret handshake…',
        'Reading the noticeboard, including the passive-aggressive notes…',
        'Shooing the pigeons off the solar panels…',
        'Waking up the servers with a strong rooibos…',
        'Fluffing the pillows nobody asked us to fluff…',
        'Polishing the buttons until we can see our reflection…',
        'Straightening the pictures in the corridor (again)…',
        'Putting the kettle on, it’s that kind of day…',
        'Taking the scenic route through the data…',
        'Wiping our feet on the welcome mat…',
        'Checking the pool is still blue…',
        'Finding the key that opens absolutely nothing…',
        'Asking the neighbour’s dog to stop narrating…',
        'Testing the doorbell with dramatic flair…',
        'Fixing the squeaky gate with a stern look…',
        'Counting the parking bays. Still one short…',
        'Hanging the welcome sign the right way up this time…',
        'Raking the leaves into a slightly neater pile…',
        'Turning it off and on again…',
        'Rolling out the red carpet (it’s a yoga mat)…',
        'Saying hello to the neighbours, and their opinions…',
        'Practising our best “welcome home” face…'
    ],

    'payments': [
        'Counting every last cent…',
        'Asking the calculator nicely…',
        'Dusting off the ledgers…',
        'Warming up the approve button…',
        'Sharpening the rubber stamp…',
        'Making sure the decimals behave…',
        'Checking twice, approving once…',
        'Herding the invoices into a queue…',
        'Polishing the rands and cents…',
        'Brewing a fresh pot of payments…',
        'Consulting the budget oracle…',
        'Putting the kettle on for the approvers…',
        'Finding out who needs a nudge…',
        'Reading the fine print, so you don’t have to…',
        'Tallying the tallies…',
        'Lining the ducks up in a row…',
        'Double-checking the suppliers’ spelling…',
        'Adding it all up (again, to be sure)…',
        'Asking the queries to form an orderly line…',
        'Getting the numbers to agree with each other…',
        'Gently waking the payment gnomes…',
        'Fetching the good pens…',
        'Teaching the invoices to queue politely…',
        'Giving the spreadsheet a pep talk…',
        'Persuading the numbers to add up…',
        'Checking the cheque is in the post…',
        'Waking up the approvals hamster…',
        'Rummaging through the in-tray…',
        'Polishing the approve button until it shines…',
        'Counting to a hundred, in rands…',
        'Asking the suppliers to say cheese…',
        'Ironing out the creases in the invoices…',
        'Finding the stapler (again)…',
        'Brewing coffee for the treasurer…',
        'Herding the decimal points…',
        'Checking nobody added an extra zero…',
        'Getting the queries off the naughty step…',
        'Making sure every cent has a home…',
        'Shaking the piggy bank, gently…',
        'Untangling the red tape…',
        'Sorting the invoices from the junk mail…',
        'Making the totals wear their Sunday best…',
        'Double-checking the double-checks…',
        'Rounding up the stragglers…',
        'Dusting the cobwebs off last month’s invoices…',
        'Teaching the invoices to sit and stay…',
        'Asking the decimal point to stop wandering off…',
        'Giving the rubber stamp a motivational speech…',
        'Checking this invoice isn’t just a takeaway menu…',
        'Negotiating with a very stubborn spreadsheet…',
        'Counting the rands, then the cents, then having a little lie-down…',
        'Resisting the urge to approve everything with a happy dance…',
        'Untangling the paper-clip chain someone made in a meeting…',
        'Making sure “R1,000” didn’t become “R1,000,000”…',
        'Asking the printer why it hates us…',
        'Finding the one pen that actually works…',
        'Telling the invoices the approvers are on their way…',
        'Bribing the payment gnomes with biltong…',
        'Locating the treasurer, last seen near the coffee…',
        'Convincing the zeros to stay where they were put…',
        'Lining up the invoices from tallest to shortest…',
        'Checking under the desk for a runaway invoice…',
        'Shouting “Next!” at the payment queue…',
        'Asking the VAT to show its working…',
        'Checking the invoices brushed their teeth…',
        'Giving each invoice a firm handshake…',
        'Teaching the rand sign to stand up straight…',
        'Rounding up the payments that went for a walk…',
        'Asking the bank to hold the line. It’s playing hold music…'
    ],

    'payment-authorisation': [
        'Working out who has the biggest pen…',
        'Tuning the approval bands. The drummer is late…',
        'Asking the debit orders to be a little less regular…',
        'Measuring the signing limits with a very long ruler…',
        'Checking which band gets top billing…',
        'Handing out backstage passes to the approvers…',
        'Reading the band rules. The band ignores them…',
        'Making sure nobody signs for more than their pocket money…',
        'Lining up the approvers by height, then by seniority…',
        'Sound-checking the authority levels: one, two, one, two…',
        'Asking the contracts if they’ve expired or are just tired…',
        'Giving every limit a little name tag…',
        'Counting the monthly commitments on fingers and toes…',
        'Persuading the standing orders to sit down…',
        'Finding out who signs off the really scary ones…',
        'Drawing the authorisation map. Here be dragons…',
        'Rehearsing the payment band’s greatest hits…',
        'Checking the conductor knows which way to wave…',
        'Matching signatures to signatories. No forgeries, please…',
        'Tidying the band’s green room…'
    ],

    // Building Documents
    'documents': [
        'Opening the filing cabinet. Mind the avalanche…',
        'Blowing the dust off the budget (achoo)…',
        'Looking behind the filing cabinet. Found R5…',
        'Unrolling the minutes, all four hours of them…',
        'Finding the AGM pack under the AGM snacks…',
        'Sharpening the paper clips, for safety reasons…',
        'Alphabetising the folders, then re-alphabetising them…',
        'Asking the conduct rules to behave themselves…',
        'Smoothing out the dog-eared pages (the dog denies everything)…',
        'Searching the archive by torchlight. Load-shedding again…',
        'Untangling the binder rings. They’re in a relationship…',
        'Opening the drawer marked “Important”. It’s mostly takeaway menus…',
        'Reading the house rules out loud in a dramatic voice…',
        'Finding the one you need. It’s always the last one…',
        'Shuffling the documents like a casino dealer…',
        'Peeking into the document vault. Smells like old paper…',
        'Ironing the newsletter flat…',
        'Asking the folders to open up about their feelings…',
        'Braving the storage room nobody likes…',
        'Counting the staples, for science…'
    ],

    'personal-documents': [
        'Opening your folder. No peeking, everyone else…',
        'Checking the envelope says your name, not your neighbour’s…',
        'Fetching your post from the pigeonhole (no pigeons involved)…',
        'Unlocking your private drawer with the tiny key…',
        'Finding your statements. They were hiding together…',
        'Steaming open nothing, we promise…',
        'Keeping your paperwork away from nosy neighbours…',
        'Ironing your statements flat…',
        'Asking your documents to smile for the camera…',
        'Rescuing your letters from the junk-mail pile…',
        'Polishing your welcome pack. It’s been waiting ages…',
        'Making sure your file didn’t run off with someone else’s…',
        'Checking nothing in your folder has grown legs…',
        'Dusting your copies with a tiny feather duster…',
        'Tiptoeing into your folder, very politely…',
        'Sorting your documents faster than you sort your socks…'
    ],

    // Document Approvals
    'docs-approval': [
        'Uncapping the good signing pen…',
        'Sticking little “sign here” arrows everywhere…',
        'Laying the drafts out like a detective’s pin board…',
        'Turning to the page with the dotted line. It’s always page 47…',
        'Warming up the approval stamp. It’s a bit stiff today…',
        'Highlighting the important bits, and the slightly less important bits…',
        'Asking the drafts to look presentable…',
        'Wrestling the documents out of the printer…',
        'Clearing space on the desk. Goodbye, coffee mugs…',
        'Collecting the trustees’ comments, and their very long comments…',
        'Practising your signature for you (just kidding)…',
        'Clipping the pages together so they don’t elope…',
        'Reading the cover page with great suspense…',
        'Placing sticky notes with dramatic urgency…',
        'Gathering everything that needs your nod. Nod responsibly…',
        'Bringing the paperwork in on a silver tray…',
        'Checking who already approved, and who will do it “just now”…'
    ],

    'financial-summary': [
        'Converting coffee into spreadsheets…',
        'Squeezing a whole year onto one page. It’s a tight fit…',
        'Boiling the books down to a tasty reduction…',
        'Giving the bottom line a pep talk…',
        'Asking the numbers for a short story, not a novel…',
        'Pinning the key figures to the board with tiny pins…',
        'Cutting together the financial highlights reel…',
        'Making the summary short, sweet and suitably dramatic…',
        'Getting the numbers to shake hands and make up…',
        'Polishing the financial pack until it sparkles…',
        'Tying the reports together with a nice bow…',
        'Rounding up the ratios. They scatter easily…',
        'Adding the final column with our tongue out in concentration…',
        'Finding the plot twist in this month’s numbers…',
        'Checking the totals agree with each other, family-dinner style…',
        'Summarising the year in fewer words than the AGM…',
        'Crunching this month’s numbers. They’re very crunchy…',
        'Making the trustees’ favourite graph go up…'
    ],

    'balance-sheet': [
        'Balancing the books on our heads…',
        'Making the balance sheet balance. It’s doing yoga…',
        'Weighing the assets and liabilities on the kitchen scale…',
        'Checking the reserves are still reserved. No gatecrashers…',
        'Looking under the couch cushions for spare assets…',
        'Levelling the books with a spirit level…',
        'Making sure every debit has a credit to dance with…',
        'Balancing a cup of tea on the balance sheet. No spills…',
        'Counting what the building owns, including the lost-property box…',
        'Counting what the building owes, with one eye closed…',
        'Asking the left side and the right side to make up…',
        'Checking the bank balance twice, in case it grew…',
        'Taking the building’s financial temperature. No fever…',
        'Reminding the equation that both sides must be equal…',
        'Checking the investments are where we left them…',
        'Squaring up the ledgers. They were looking a bit round…',
        'Holding both sides of the scale very, very still…'
    ],

    'income-statement': [
        'Counting what came in, with a little happy dance…',
        'Counting what went out, with a little sad dance…',
        'Checking the electricity bill through our fingers…',
        'Asking the water bill to please calm down…',
        'Finding out where every rand went. One ran off with the garden service…',
        'Teaching the columns to add up. They’re slow learners…',
        'Asking the accountant’s calculator nicely…',
        'Grouping the expenses into families, with a family photo…',
        'Totting up the levies and celebrating each one…',
        'Tallying the maintenance costs. The pool pump says sorry…',
        'Lining up the income and expenses for a staring contest…',
        'Working out the surplus, fingers crossed…',
        'Adding up the cleaning bills. Sparkling…',
        'Reading the insurance premium twice. Not a typo…',
        'Checking the profit and loss without crying…',
        'Counting the security costs, very securely…',
        'Convincing the expenses they don’t all need to be this big…'
    ],

    'cashflow-statement': [
        'Following the money. It runs surprisingly fast…',
        'Making sure the cashflow flows and doesn’t just trickle…',
        'Checking the money didn’t go for a swim…',
        'Charting the mighty River Rand…',
        'Paddling downstream with the cash…',
        'Checking the taps aren’t dripping rands…',
        'Measuring the inflows with a bucket…',
        'Measuring the outflows with a bigger bucket…',
        'Mapping the money’s journey. It took the long way…',
        'Untangling the transfers. They’re worse than garden hoses…',
        'Timing the levies coming in, stopwatch ready…',
        'Plotting the bank balance on a rollercoaster…',
        'Opening the floodgates, very gently…',
        'Working out the closing balance, and closing the door quietly…',
        'Asking the cash where it’s been all month…',
        'Following the cash with a very small magnifying glass…'
    ],

    'cashflow-statement-new': [
        'Drawing the new cashflow map, with a treasure X…',
        'Sketching the money’s route on a napkin…',
        'Getting the columns to march in step: left, right, left…',
        'Unrolling the cashflow scroll. It’s a long one…',
        'Plotting the peaks and the dips. Hold on tight…',
        'Charting the ins and outs, and the shake-it-all-abouts…',
        'Untangling the year-to-date figures. They knotted overnight…',
        'Polishing the shiny new report…',
        'Putting the cash in the right buckets. No splashing…',
        'Tracing every transfer with a crayon…',
        'Laying the months out like a picnic blanket…',
        'Asking the new report to make a good first impression…',
        'Following the rands through the year. They’re very fit…',
        'Lining up income against spending. Ding ding, round one…',
        'Teaching the old numbers new tricks…'
    ],

    'income-vs-budget': [
        'Comparing the budget with reality. Reality is winning…',
        'Holding the budget up to the light…',
        'Seeing who stuck to the plan, and who “improvised”…',
        'Applauding the lines that came in under budget…',
        'Having a quiet word with the lines that went over…',
        'Counting the surprises, nice ones first…',
        'Checking the budget’s crystal ball for cracks…',
        'Marking the budget’s homework, red pen ready…',
        'Measuring the gap between plan and practice with a tape measure…',
        'Asking the overspends to explain themselves…',
        'Matching each actual to its budget line, dating-show style…',
        'Looking for the unexpected expenses. They’re good at hiding…',
        'Seeing how the year is tracking. Recalculating…',
        'Checking the budget kept its New Year’s resolutions…',
        'Putting the budget and the actuals in the same room…',
        'Plotting this year against last year. May the best year win…',
        'Giving the forecast a gold star for trying…'
    ],

    'levy-roll': [
        'Calling the levy roll. Everybody say “present”…',
        'Taking attendance for the levies. No bunking…',
        'Knocking on each unit’s levy door, politely…',
        'Ticking off the paid units with a very satisfying tick…',
        'Rounding up the levies like a friendly sheepdog…',
        'Reconciling the ledgers. They had a falling-out…',
        'Lining up the units in order. Unit 7 keeps cutting in…',
        'Lining up the debits and credits for a group photo…',
        'Checking the special levies are feeling special…',
        'Matching receipts to units, like socks from the wash…',
        'Clearing our throat to read the roll from the top…',
        'Tallying the month’s collections. Cha-ching…',
        'Making sure every unit is on the list, even the shy ones…',
        'Finding each owner’s balance without a treasure map…',
        'Counting the levies collected, with jazz hands…',
        'Polishing each unit’s little line on the roll…'
    ],

    'debtor-age-analysis': [
        'Counting the days on a very long calendar…',
        'Sorting the balances by age. The oldest gets the comfy chair…',
        'Lining up 30, 60, 90 days and beyond…',
        'Lighting the birthday candles on the old balances…',
        'Measuring the arrears with a very long ruler…',
        'Finding the balances that are getting on a bit…',
        'Asking the oldest balances how they stay so young…',
        'Sorting current from overdue, like laundry…',
        'Totting up the 120-day column. It’s a long-distance runner…',
        'Writing reminders in our friendliest voice…',
        'Making sure no balance sneaks out the back door…',
        'Checking the calendar. It’s further along than we thought…',
        'Finding the accounts that might like a gentle nudge…',
        'Adding up the overdue amounts with a sympathetic face…',
        'Dusting off the very oldest balance. It has a beard now…'
    ],

    'wages-roll': [
        'Counting the payslips, then counting them again…',
        'Checking the overtime. Someone worked a lot of Sundays…',
        'Adding up the hours, and the tea breaks…',
        'Making sure everyone gets paid, especially the caretaker…',
        'Calculating the deductions. The taxman says hi…',
        'Checking the UIF. It’s very insistent…',
        'Thanking the cleaners while we count…',
        'Admiring the handwriting on this month’s timesheets…',
        'Checking for a bonus month, fingers crossed for everyone…',
        'Lining up the salary slips for a very boring parade…',
        'Totting up the garden staff wages. The roses say thanks…',
        'Looking up the leave balances. Someone deserves a holiday…',
        'Matching payslips to people, not to the building cat…',
        'Checking the payroll calendar. Payday is coming…'
    ],

    'contractors-roll': [
        'Flipping through the directory of “a guy who knows a guy”…',
        'Checking who fixes what, and who will come “tomorrow”…',
        'Finding the plumbers. They’re under a sink somewhere…',
        'Asking the electricians if it’s safe to touch this…',
        'Sorting the toolboxes by trade, and by rust…',
        'Finding a contractor with a free afternoon (rare)…',
        'Lining up the tradespeople, hard hats on…',
        'Counting the call-outs. The geyser called twice…',
        'Adding up the plumbing bills. They’re a bit leaky…',
        'Tallying the garden service. The hedge says thanks…',
        'Matching contractors to their jobs, and to their bakkies…',
        'Sorting out the painters’ invoices. They’re a bit splattered…',
        'Adding up the security company’s bills, guardedly…',
        'Checking the totals with a spanner…',
        'Asking around for recommendations. Uncle says he can do it…',
        'Measuring twice, invoicing once…'
    ],

    // Also compliance.fire, compliance.mini-sub and compliance.lift
    'compliance': [
        'Checking the fire extinguishers are still extinguishing…',
        'Testing the alarm, quietly. Sorry, neighbours…',
        'Counting the smoke detectors. They counted us back…',
        'Rolling up the fire hoses. They’re tangled like earphones…',
        'Inspecting the inspection reports. Very meta…',
        'Asking the lift to go up and down. It went sideways…',
        'Checking the fire doors close, dramatically…',
        'Testing the sprinklers without getting wet (failed)…',
        'Peeking inside the mini-sub. It’s humming a tune…',
        'Checking the certificates are current, not vintage…',
        'Making sure the exit signs point to the exit…',
        'Reading the lift certificate. It’s had its ups and downs…',
        'Ticking off the safety list. Safety first, ticking second…',
        'Counting the emergency lights during load-shedding…',
        'Catching the next inspection date before it sneaks up on us…',
        'Making sure everything is up to code, and not just Morse code…',
        'Asking the fire quote to cool down…'
    ],

    // My Account / statements
    'accounts-history': [
        'Rewinding your account, VHS style…',
        'Flipping through your transactions like a photo album…',
        'Finding your latest balance. It was hiding under last month…',
        'Matching every payment to its invoice, like a dating app…',
        'Adding up what’s been paid, with a round of applause…',
        'Taking roll call for every rand…',
        'Searching your history. No embarrassing photos, promise…',
        'Checking every last levy is accounted for…',
        'Pulling up your statement archive. Mind the dust…',
        'Finding the month you’re after. It was in a different drawer…',
        'Totting up your payments this year. You’ve been busy…',
        'Checking your interest charges with a raised eyebrow…',
        'Reconciling the receipts. They had a little argument…',
        'Tidying up your transaction history. It was a bit messy…',
        'Counting the debits and credits: one, two, many…'
    ],

    // Building Information
    'utility': [
        'Looking up the municipality’s number, then waiting on hold…',
        'Finding out who to call about the lights…',
        'Checking the load-shedding schedule, and the candles…',
        'Looking up who to call when the geyser bursts (hopefully never)…',
        'Reading the meter. It’s a dramatic story…',
        'Finding refuse collection day. The bins want to know…',
        'Finding who fixes the pool pump. It’s sulking again…',
        'Checking who looks after the gate, and its moods…',
        'Finding the pest control number. The ants are unionising…',
        'Sticking the emergency numbers on the fridge…',
        'Asking the caretaker for the latest gossip, er, updates…',
        'Looking up the after-hours number. It’s after hours somewhere…',
        'Finding who fixes the lift. It’s feeling a bit down…',
        'Checking the access control. The remote is in the car…',
        'Looking up the security company. They’re very secretive…',
        'Finding the garden service. The hedge is getting ideas…',
        'Checking the water meter. It spins like a disco ball…'
    ],

    // My Details
    'my-profile': [
        'Checking we spelt your name right this time…',
        'Straightening your name badge…',
        'Polishing your profile picture frame…',
        'Making sure we know where to reach you (not in a creepy way)…',
        'Looking up your preferences: extra milk, no sugar…',
        'Finding your phone number. We won’t prank call…',
        'Checking your email address. It’s a good one…',
        'Making your profile feel at home. Slippers out…',
        'Laying out your details like a nice outfit…',
        'Looking up all your buildings. Nice…',
        'Checking your trustee roles. Very important person…',
        'Asking your profile to smile for the photo…',
        'Making sure your details are fresher than our bread…',
        'Getting your settings just the way you like them…',
        'Practising your name in our best announcer voice…'
    ],

    'tenant': [
        'Knocking on the right doors, politely…',
        'Checking who lives where, without being nosy…',
        'Matching tenants to their units, and to their keys…',
        'Making sure the welcome mat is out and spelt correctly…',
        'Updating the pets register. The parrot insists on being listed…',
        'Checking who has the access tags, and who keeps losing them…',
        'Counting the keys. There’s always one mystery key…',
        'Looking up the parking allocations. No double-parking…',
        'Checking the house rules were signed, not just doodled on…',
        'Checking the move-in dates. The boxes are still unpacked…',
        'Reading the lease details. It’s a page-turner…',
        'Gathering the occupant records. Everyone say cheese…',
        'Checking the deposit is sitting safely…',
        'Looking up the move-out dates. Sniff…',
        'Finding the tenants’ numbers. No prank calls…'
    ],

    'audits-capture': [
        'Sharpening the auditor’s pencil to a dangerous point…',
        'Following the paper trail. It goes round in circles…',
        'Cross-referencing the cross-references…',
        'Ticking the boxes: tick, tick, tick… ooh, a cross…',
        'Making sure the numbers tell the whole truth…',
        'Lining up the evidence like a detective show…',
        'Matching the vouchers. No lonely vouchers allowed…',
        'Checking the supporting documents are being supportive…',
        'Asking the general ledger to take the stand…',
        'Reading the management responses, and between the lines…',
        'Pulling the trial balance. It’s on trial…',
        'Checking the bank confirmations. The bank confirms it’s a bank…',
        'Laying out the working papers. They’re working hard…',
        'Looking for the audit trail. It went for a hike…',
        'Asking the queries to state their names for the record…',
        'Putting on our most serious auditor face…'
    ],

    'auditors-dashboard': [
        'Sharpening the red pencil…',
        'Polishing the auditors’ magnifying glass…',
        'Sorting the audits from “nearly done” to “where do we start”…',
        'Counting the open queries, and the very open queries…',
        'Checking which audits are due, and which are overdue…',
        'Colour-coding the audit calendar…',
        'Tapping our feet at the audits waiting on answers…',
        'Putting the audits in order of urgency. Red ones first…',
        'Setting the audit agenda. Item one: coffee…',
        'Checking who has signed off, and who has nodded off…',
        'Looking up the year-ends. They come round every year…',
        'Counting the outstanding documents. Deep breath…',
        'Checking which queries got replies, and which got ghosted…',
        'Opening the auditors’ desk drawer. It’s all highlighters…'
    ],

    'register': [
        'Printing your name tag, spelt right…',
        'Cutting your key. It’s a very digital key…',
        'Making space for you. Budge up, everyone…',
        'Rolling out the extra-fluffy welcome mat…',
        'Shaking your hand, virtually. Firm but friendly…',
        'Checking the guest list. You’re on it…',
        'Polishing a brand-new account. Still has that new-account smell…',
        'Putting your name on the door in gold letters…',
        'Baking a welcome cake (sadly imaginary)…',
        'Telling the other accounts to be nice to the new one…',
        'Making coffee for the new arrival…',
        'Packing your welcome pack. There are stickers…',
        'Blowing up a balloon for you, slowly…',
        'Warming up the “Welcome!” banner…'
    ],

    // Links from e-mails to the old portal
    'historical-link': [
        'Following the link down the rabbit hole…',
        'Dusting off an old address…',
        'Translating “old link” into “new link”…',
        'Following the breadcrumbs. A pigeon ate some…',
        'Checking this link doesn’t lead to Narnia…',
        'Stopping at the petrol station to ask for directions…',
        'Taking the shortcut, which is longer…',
        'Finding the right door for this link. It’s door number three…',
        'Redirecting you, in our best GPS voice…',
        'Finding out where this link went on holiday…',
        'Blowing the cobwebs off this link…'
    ],

    'clearance-capture': [
        'Getting the keys ready for the new owner, all seventeen of them…',
        'Calculating the clearance amount. The attorneys are watching…',
        'Working out the pro-rata levies with a protractor…',
        'Circling the transfer date in red…',
        'Lining up the sale paperwork. There’s a lot of it…',
        'Fetching the conveyancer’s details. They’re very busy…',
        'Checking for sneaky special levies…',
        'Adding up what’s owed before the big move…',
        'Preparing the certificate, and the confetti…',
        'Opening the clearance file. Mind the paper cuts…',
        'Waving goodbye to the seller’s account…',
        'Checking the attorneys’ references. They reference each other…',
        'Double-checking the transfer checklist’s checklist…',
        'Polishing the “Sold” sign…',
        'Making sure the levies didn’t pack themselves into a moving box…',
        'Saving the new owner the good parking bay…'
    ],

    'clearance-request': [
        'Finding the right form. There are many forms…',
        'Filling in the form in our neatest handwriting…',
        'Checking what we need from you. Nothing scary…',
        'Cutting the ribbon on a new clearance request…',
        'Getting the request ready to send. It’s packing its bags…',
        'Finding the transfer forms behind the other forms…',
        'Checking the requirements, all twelve of them…',
        'Laying out the form so it’s easy, promise…',
        'Finding a pen that works for the important bits…',
        'Making the request look its very best…'
    ],

    'rentals-capture': [
        'Measuring the windows for curtains…',
        'Checking the pet policy. The goldfish is fine…',
        'Getting the keys ready for the new tenant. Don’t lose them…',
        'Fetching the application form. It’s a few pages…',
        'Checking the move-in date. The truck is booked…',
        'Looking up the rental amount. Deep breath…',
        'Checking the references. They say lovely things…',
        'Preparing the welcome pack, with extra welcome…',
        'Reading the meters. The electricity is curious too…',
        'Picking a parking bay, not the one by the bins…',
        'Checking the inspection. That scuff was already there…',
        'Gathering the supporting documents, and some moral support…',
        'Opening the rentals file. New-tenant smell…',
        'Fluffing the cushions in the show unit…',
        'Checking the available units. One has a lovely view of the car park…'
    ]
};

// Loaders inside a page: <section class="main-busy-indicator" data-loading-phrases="<key>">
export const PANELS = {
    // The History tab, desktop and mobile
    'payments.history': [
        'Rewinding the payment tape. Be kind, rewind…',
        'Digging through the payments time capsule…',
        'Taking a stroll down payment memory lane…',
        'Dusting off last year’s invoices. Bless you…',
        'Asking the archivist for the next page. She’s on her tea break…',
        'Finding the receipts in the bottom drawer, under the pizza coupons…',
        'Looking up who signed what, and with which pen…',
        'Reading yesterday’s news, payment by payment…',
        'Turning the ledger’s pages carefully. It’s very old…',
        'Following the footprints of finished approvals…',
        'Fetching the ladder for the files on the top shelf…',
        'Retracing the paper trail. It went everywhere…',
        'Bringing back the paid and the cancelled for a reunion…',
        'Checking the history books. No spoilers…',
        'Opening the payments archive. It creaks…',
        'Searching the archive one page at a time, sloooowly…',
        'Asking past payments how they’ve been…',
        'Leafing back through old approvals. Ah, memories…'
    ],

    'accounts-history.details': [
        'Zooming in… enhance… enhance…',
        'Peeking behind the number…',
        'Unpacking the transaction. Mind the polystyrene…',
        'Reading the fine print with a magnifying glass…',
        'Looking up the reference number. It’s a long one…',
        'Coaxing out the invoice behind the entry. It’s shy…',
        'Checking who processed it. They say hi…',
        'Expanding the details like a pop-up book…',
        'Checking the date on the receipt, and the coffee stain…',
        'Fetching the supporting details. They’re very supportive…'
    ],

    'tenant.details': [
        'Opening the tenant’s file, gently…',
        'Unfolding the lease agreement. It keeps unfolding…',
        'Reading the tenant record with appropriate respect…',
        'Checking the lease is holding up well…',
        'Finding the tenant’s unit. It’s the one with the plants…',
        'Checking the contact details, all ten digits…',
        'Pulling the tenant record from the cabinet with tweezers…',
        'Peeking at the tenant’s file, very discreetly…'
    ],

    'levy-roll.memo': [
        'Reading the notes in the margin, and the doodles…',
        'Opening the memo. It’s on a sticky note…',
        'Deciphering someone’s handwriting…',
        'Unfolding the memo. It’s been folded twelve times…',
        'Looking up the payment arrangement, handshake included…',
        'Finding the last note on file, under a coffee ring…',
        'Reading the unit’s history. It’s a page-turner…',
        'Reading the sticky notes before they unstick…'
    ],

    'debtor-age-analysis.memo': [
        'Opening the account notes. They’re very detailed…',
        'Finding the payment plan. It’s a good plan…',
        'Checking the follow-up dates on the fridge calendar…',
        'Reading the correspondence log. The plot thickens…',
        'Unfolding the arrangement letter. It’s been through a lot…',
        'Looking up the last reminder. It was very polite…',
        'Fetching the notes from the “to do” pile…',
        'Squinting at the handwriting on the last note…'
    ],

    'financial-summary.mailing-list': [
        'Licking the envelopes (digitally, thankfully)…',
        'Fetching the mailing list, and a stamp…',
        'Checking nobody’s been left off the list again…',
        'Writing the address labels in our neatest handwriting…',
        'Waving the postman over…',
        'Counting the stamps. There are never enough…',
        'Sorting the recipients from A to Zzz…',
        'Making sure the monthly pack goes to people, not to the cat…'
    ],

    'clearance-capture.purchaser': [
        'Welcoming the new owner, in advance…',
        'Looking up the purchaser. They seem nice…',
        'Fetching the purchaser’s details, with a firm handshake…',
        'Checking the buyer’s number. No wrong numbers…',
        'Reading the offer to purchase. Very exciting…',
        'Opening the purchaser file. Fresh and new…',
        'Choosing a housewarming gift, mentally…'
    ],

    'clearance-capture.attachment': [
        'Attaching the documents with digital sticky tape…',
        'Stapling the attachments. Ka-chunk…',
        'Uploading the paperwork. Up, up, up…',
        'Checking the files landed safely. No turbulence…',
        'Sending the documents along by carrier pigeon…',
        'Filing the attachments, in the right file this time…',
        'Making sure the upload didn’t trip on the way…'
    ],

    'rentals-capture.upload': [
        'Uploading the application. Wheee…',
        'Carrying the documents across. Mind the step…',
        'Sealing the envelope, digitally. No licking…',
        'Handing the files to the Rentals team. They say thanks…',
        'Sending the documents by very fast pigeon…',
        'Packing the paperwork into the upload van…',
        'Delivering the application. Ding dong…',
        'Checking every file made it. Roll call…'
    ]
};

// A screen with no list above
export const FALLBACK = [
    'Asking the server nicely…',
    'Doing a little loading dance…',
    'Counting to ten, slowly…',
    'Feeding the hamsters that power the internet…',
    'Untangling the wires behind the server…',
    'Warming up the pixels…',
    'Pressing the buttons in the right order this time…',
    'Shaking the internet, gently…'
];
